/**
 * watchdogStore — estado do Watchdog neste computador (decisões 187 a 194 e 244 a 248).
 *
 * Fases: `idle` (ainda não perguntou), `checking`, `off` (servidor sem Watchdog para esta conta,
 * ou rota fora do ar), `active` (com a vez), `paused` (outro computador está em uso, ou a troca
 * espera: `wait`), `authorize` (pedido de acesso aberto), `refused` e `expired`.
 * A lógica de rede fica em features/account/useWatchdog.ts.
 */
import { create } from 'zustand';
import type {
  WatchdogDevice,
  WatchdogRequest,
  WatchdogSession,
  WatchdogWait,
} from '../api/watchdog';

/** Troca em espera (decisão 245): a partir de quando este computador pode pegar a vez. */
export interface SwitchWait {
  availableAt: string;
  waitHours: number;
  lastUseDevice: WatchdogDevice | null;
}

/** A espera que veio do servidor, se houver. */
export function waitOf(data: WatchdogWait): SwitchWait | null {
  if (!data.availableAt) return null;
  return {
    availableAt: data.availableAt,
    waitHours: data.waitHours ?? 12,
    lastUseDevice: data.lastUseDevice ?? null,
  };
}

export type WatchdogPhase =
  | 'idle'
  | 'checking'
  | 'off'
  | 'active'
  | 'paused'
  | 'authorize'
  | 'refused'
  | 'expired';

interface WatchdogStoreState {
  phase: WatchdogPhase;
  /** O Watchdog vale para esta conta (o menu mostra Computadores). */
  enabled: boolean;
  device: WatchdogDevice | null;
  /** Quem está com a vez, quando não é este computador. */
  activeDevice: WatchdogDevice | null;
  request: WatchdogRequest | null;
  devices: WatchdogDevice[];
  /** Troca em espera, na pausa. */
  wait: SwitchWait | null;
  /** Pedidos de acesso esperando decisão (aparecem no computador com a vez). */
  pending: WatchdogRequest[];
  emailSent: boolean | null;
  /** Outro computador pegou a vez com o painel aberto aqui: janela por cima, sem trocar de tela. */
  pausedWhileUsing: boolean;
  apply: (session: WatchdogSession) => void;
  set: (partial: Partial<Omit<WatchdogStoreState, 'apply' | 'set' | 'reset'>>) => void;
  reset: () => void;
}

const INITIAL = {
  phase: 'idle' as WatchdogPhase,
  enabled: false,
  device: null,
  activeDevice: null,
  request: null,
  devices: [] as WatchdogDevice[],
  wait: null as SwitchWait | null,
  pending: [] as WatchdogRequest[],
  emailSent: null,
  pausedWhileUsing: false,
};

export const useWatchdogStore = create<WatchdogStoreState>((set) => ({
  ...INITIAL,
  apply: (session) =>
    set((state) => {
      switch (session.state) {
        case 'off':
          return { ...INITIAL, phase: 'off' };
        case 'active':
          return {
            phase: 'active',
            enabled: true,
            device: session.device,
            activeDevice: null,
            request: null,
            pending: session.pending ?? [],
            wait: null,
            pausedWhileUsing: false,
          };
        case 'paused':
          return {
            phase: 'paused',
            enabled: true,
            device: session.device ?? state.device,
            activeDevice: session.activeDevice,
            wait: waitOf(session),
          };
        case 'authorize':
          return {
            phase: 'authorize',
            enabled: true,
            request: session.request,
            devices: session.devices,
            emailSent: session.emailSent ?? state.emailSent,
          };
        case 'refused':
          return {
            phase: 'refused',
            enabled: true,
            devices: session.devices,
          };
        default:
          return {};
      }
    }),
  set: (partial) => set(partial),
  reset: () => set({ ...INITIAL }),
}));

/** O painel pode mostrar o conteúdo: Watchdog desligado ou este computador com a vez. */
export function watchdogAllowsApp(phase: WatchdogPhase): boolean {
  return phase === 'off' || phase === 'active';
}
